import { Component } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RequestService } from '../services/request.service';
import { ProjectService, Project } from '../services/project.service';
import { finalize } from 'rxjs/operators';
import { SelectedProjectService } from '../services/selectedproject.service';
import { AuthService } from '../services/auth.service';

export interface CreateRequestDto {
  projectNumber: string;
  description: string;
  status: string;
  createdBy: string;
  requestType: string;
  revisionText: string;
  priority: string;
  area: string;
  sequence: string;
}

@Component({
  selector: 'app-create-request',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatIconModule],
  templateUrl: './create-request.component.html',
  styleUrl: './create-request.component.css'
})
export class CreateRequestComponent {
  request: CreateRequestDto = {
    projectNumber: '',
    description: '',
    status: 'Open',
    createdBy: '',
    requestType: '',
    revisionText: '',
    priority: 'Medium',
    area: '',
    sequence: ''
  };

  revisionText = '';

  projectName: string | null = null;
  loading = false;
  success = false;
  error: string | null = null;
  uploadProgress = false;
  selectedFiles: File[] = [];
  projects: Project[] = [];
  projectsLoading = false;
  allowedRequestTypes: string[] = ['RFI', 'CDQ', 'CRFI', 'IQ'];
  limitedToIq = false;

  constructor(
    private requestService: RequestService,
    private projectService: ProjectService,
    private router: Router,
    private selectedProjectService: SelectedProjectService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.limitedToIq = this.authService.hasAnyRole(['Offshore', 'User','OutSourceAdmin','OutsourcePM','OutsourceUser']);

    if (this.limitedToIq) {
      this.allowedRequestTypes = ['IQ'];
      this.request.requestType = 'IQ';
    } else if (this.authService.hasAnyRole(['Admin'])) {
      this.allowedRequestTypes = ['RFI', 'IQ'];
    }

    // Status is always Open when creating
    this.request.status = 'Open';

    this.loadProjects();
    
    // Check for a selected project
    const selectedProject = this.selectedProjectService.getSelectedProject();
    if (selectedProject) {
      this.request.projectNumber = selectedProject.toString();
      this.projectSelected();
    }
  }

  loadProjects(): void {
    this.projectsLoading = true;
    this.projectService.getAllProjects().subscribe({
      next: (projects) => {
        this.projects = projects;
        this.projectsLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load projects';
        this.projectsLoading = false;
        console.error(err);
      }
    });
  }

  projectSelected(): void {
    if (!this.request.projectNumber) {
      this.projectName = null;
      return;
    }

    const selectedProject = this.projects.find(p => p.projectNumber.toString() === this.request.projectNumber);
    if (selectedProject) {
      this.projectName = selectedProject.projectName;
      this.error = null;
    } else {
      this.projectName = null;
    }
  }

  createRequest(): void {
    this.loading = true;
    this.error = null;

    const requestType = this.limitedToIq ? 'IQ' : this.request.requestType;
    const status = 'Open';

    const payload: CreateRequestDto = {
      projectNumber: this.request.projectNumber,
      description: this.request.description,
      status,
      createdBy: localStorage.getItem('id') || '',
      requestType,
      revisionText: this.revisionText,
      priority: this.request.priority,
      area: this.request.area,
      sequence: this.request.sequence
    };

    this.requestService.createRequest(payload).pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: (createdRequest) => {
        // Step 2: Create Revision 0 with the revision text and optional file
        this.uploadProgress = true;
        const userId = localStorage.getItem('id') || '';
        this.requestService.createRevision(
          createdRequest.requestGuid.toString(),
          this.revisionText,
          userId,
          this.selectedFiles.length > 0 ? this.selectedFiles : undefined,
          0
        ).pipe(
          finalize(() => {
            this.uploadProgress = false;
            this.success = true;
            setTimeout(() => this.router.navigate(['/requests']), 1500);
          })
        ).subscribe({
          next: () => {},
          error: (err) => {
            console.error(err);
            this.error = 'Request created, but revision creation failed.';
          }
        });
      },
      error: (err) => {
        console.error(err);
        this.error = 'Failed to create request.';
      }
    });
  }

  isDragOver = false;

  onFileSelected(event: any): void {
    const element = event.target as HTMLInputElement;
    if (element.files && element.files.length > 0) {
      for (let i = 0; i < element.files.length; i++) {
        this.selectedFiles.push(element.files[i]);
      }
    }
    element.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        this.selectedFiles.push(event.dataTransfer.files[i]);
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }
}
